import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { Plus, X, Trash2, Edit3, Image as ImageIcon, Heart, MessageSquare, Save, ChevronDown, ChevronUp } from 'lucide-react';

const CATEGORIES = [
    '지르코니아', '임플란트', 'PFM', '라미네이트', '틀니',
    '인레이/온레이', '교정장치', '크라운/브릿지', 'CAD/CAM', '기타'
];

function LabPortfolioPosts({ user }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [saving, setSaving] = useState(false);

    // Form state
    const [formPhotos, setFormPhotos] = useState([]);
    const [formPreviews, setFormPreviews] = useState([]);
    const [formCategory, setFormCategory] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formTags, setFormTags] = useState('');

    const loadPosts = useCallback(async () => {
        if (!user?.uid) return;
        setLoading(true);
        try {
            const q = query(
                collection(db, 'labPosts'),
                where('labId', '==', user.uid),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error('포트폴리오 로드 실패:', e);
        }
        setLoading(false);
    }, [user?.uid]);

    useEffect(() => { loadPosts(); }, [loadPosts]);

    const resetForm = () => {
        setFormPhotos([]);
        setFormPreviews([]);
        setFormCategory('');
        setFormDescription('');
        setFormTags('');
        setEditingPost(null);
        setShowForm(false);
    };

    const handlePhotoSelect = (e) => {
        const files = Array.from(e.target.files);
        if (formPreviews.length + files.length > 5) {
            alert('사진은 최대 5장까지 가능합니다.');
            return;
        }
        const newPreviews = files.map(f => URL.createObjectURL(f));
        setFormPhotos(prev => [...prev, ...files]);
        setFormPreviews(prev => [...prev, ...newPreviews]);
    };

    const removePhoto = (idx) => {
        setFormPhotos(prev => prev.filter((_, i) => i !== idx));
        setFormPreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const startEdit = (post) => {
        setEditingPost(post);
        setFormPreviews(post.photos || []);
        setFormPhotos([]);
        setFormCategory(post.category || '');
        setFormDescription(post.description || '');
        setFormTags((post.tags || []).join(', '));
        setShowForm(true);
    };

    const handleSubmit = async () => {
        if (formPreviews.length === 0) {
            alert('사진을 1장 이상 올려주세요.');
            return;
        }
        if (!formCategory) {
            alert('카테고리를 선택해주세요.');
            return;
        }
        setSaving(true);
        try {
            // Upload new photos
            let photoUrls = [];
            if (editingPost) {
                // Keep existing URLs that are still in previews
                photoUrls = formPreviews.filter(p => p.startsWith('http'));
            }
            for (const file of formPhotos) {
                const fileRef = ref(storage, `labPosts/${user.uid}/${Date.now()}_${file.name}`);
                await uploadBytes(fileRef, file);
                const url = await getDownloadURL(fileRef);
                photoUrls.push(url);
            }

            const tags = formTags.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean);

            // 기공소 이름/지역 가져오기
            let labName = user.companyName || user.labName || user.displayName || '';
            let labRegion = user.address ? user.address.split(' ').slice(0, 2).join(' ') : '';
            try {
                const adDoc = await getDoc(doc(db, 'labAdvertisements', user.uid));
                if (adDoc.exists()) {
                    const ad = adDoc.data();
                    if (ad.businessName) labName = ad.businessName;
                    if (ad.address) labRegion = ad.address.split(' ').slice(0, 2).join(' ');
                }
                if (!labName) {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists()) {
                        const u = userDoc.data();
                        labName = u.companyName || u.businessName || u.displayName || '';
                        if (!labRegion && u.address) labRegion = u.address.split(' ').slice(0, 2).join(' ');
                    }
                }
            } catch (e) { /* ignore */ }

            const postData = {
                labId: user.uid,
                labName,
                labRegion,
                photos: photoUrls,
                category: formCategory,
                description: formDescription,
                tags,
                likes: editingPost?.likes || 0,
                commentCount: editingPost?.commentCount || 0,
            };

            if (editingPost) {
                await updateDoc(doc(db, 'labPosts', editingPost.id), {
                    ...postData,
                    updatedAt: serverTimestamp(),
                });
            } else {
                postData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'labPosts'), postData);
            }

            resetForm();
            loadPosts();
        } catch (e) {
            console.error('게시물 저장 실패:', e);
            alert('저장에 실패했습니다.');
        }
        setSaving(false);
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('이 게시물을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'labPosts', postId));
            setPosts(prev => prev.filter(p => p.id !== postId));
        } catch (e) {
            console.error('삭제 실패:', e);
        }
    };

    const totalLikes = posts.reduce((sum, p) => sum + (p.likes || 0), 0);

    return (
        <div>
            {/* 통계 */}
            <div style={S.statsRow}>
                <div style={S.statBox}>
                    <div style={S.statNum}>{posts.length}</div>
                    <div style={S.statLabel}>게시물</div>
                </div>
                <div style={S.statBox}>
                    <div style={S.statNum}>{totalLikes}</div>
                    <div style={S.statLabel}>좋아요</div>
                </div>
            </div>

            {/* 새 게시물 버튼 */}
            {!showForm && (
                <button onClick={() => setShowForm(true)} style={S.addBtn}>
                    <Plus size={18} /> 새 게시물 작성
                </button>
            )}

            {/* 게시물 작성/수정 폼 */}
            {showForm && (
                <div style={S.formCard}>
                    <div style={S.formHeader}>
                        <h3 style={S.formTitle}>{editingPost ? '게시물 수정' : '새 게시물'}</h3>
                        <button onClick={resetForm} style={S.closeBtn}><X size={18} /></button>
                    </div>

                    {/* 사진 업로드 */}
                    <div style={S.formGroup}>
                        <label style={S.label}>사진 (최대 5장)</label>
                        <div style={S.photoGrid}>
                            {formPreviews.map((url, i) => (
                                <div key={i} style={S.photoItem}>
                                    <img src={url} alt="" style={S.photoImg} />
                                    <button onClick={() => removePhoto(i)} style={S.photoRemove}><X size={12} /></button>
                                </div>
                            ))}
                            {formPreviews.length < 5 && (
                                <label style={S.photoAdd}>
                                    <Plus size={24} color="#94a3b8" />
                                    <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} style={{ display: 'none' }} />
                                </label>
                            )}
                        </div>
                    </div>

                    {/* 카테고리 */}
                    <div style={S.formGroup}>
                        <label style={S.label}>보철 종류</label>
                        <div style={S.catGrid}>
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setFormCategory(cat)}
                                    style={{
                                        ...S.catPill,
                                        ...(formCategory === cat ? S.catPillActive : {}),
                                    }}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 설명 */}
                    <div style={S.formGroup}>
                        <label style={S.label}>설명</label>
                        <textarea
                            value={formDescription}
                            onChange={e => setFormDescription(e.target.value)}
                            placeholder="작업물에 대한 설명을 작성하세요"
                            style={S.textarea}
                            rows={3}
                        />
                    </div>

                    {/* 태그 */}
                    <div style={S.formGroup}>
                        <label style={S.label}>태그 (쉼표로 구분)</label>
                        <input
                            value={formTags}
                            onChange={e => setFormTags(e.target.value)}
                            placeholder="지르코니아, 심미보철, 쉐이드매칭"
                            style={S.input}
                        />
                    </div>

                    {/* 저장 */}
                    <button onClick={handleSubmit} disabled={saving} style={S.submitBtn}>
                        <Save size={16} />
                        {saving ? '저장 중...' : editingPost ? '수정 완료' : '게시하기'}
                    </button>
                </div>
            )}

            {/* 게시물 목록 */}
            {loading ? (
                <div style={S.loadingText}>로딩 중...</div>
            ) : posts.length === 0 ? (
                <div style={S.emptyState}>
                    <ImageIcon size={40} color="#d1d5db" />
                    <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '8px' }}>아직 게시물이 없습니다</p>
                    <p style={{ color: '#cbd5e1', fontSize: '12px' }}>작업물 사진을 올려 치과에 홍보하세요</p>
                </div>
            ) : (
                <div style={S.postList}>
                    {posts.map(post => (
                        <PostCard key={post.id} post={post} onEdit={startEdit} onDelete={handleDelete} />
                    ))}
                </div>
            )}
        </div>
    );
}

function PostCard({ post, onEdit, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const mainPhoto = post.photos?.[0];
    const extraCount = (post.photos?.length || 0) - 1;
    const timeAgo = post.createdAt?.toDate
        ? formatTimeAgo(post.createdAt.toDate())
        : '';

    return (
        <div style={S.postCard}>
            <div style={S.postTop}>
                {/* 사진 */}
                <div style={S.postImgWrap}>
                    {mainPhoto && <img src={mainPhoto} alt="" style={S.postImg} />}
                    {extraCount > 0 && (
                        <div style={S.postImgBadge}>+{extraCount}</div>
                    )}
                </div>
                {/* 정보 */}
                <div style={S.postInfo}>
                    <div style={S.postCategory}>{post.category}</div>
                    <div style={S.postDesc}>{post.description || '설명 없음'}</div>
                    <div style={S.postMeta}>
                        <span style={S.postStat}><Heart size={13} color="#ef4444" /> {post.likes || 0}</span>
                        <span style={S.postStat}><MessageSquare size={13} color="#6366f1" /> {post.commentCount || 0}</span>
                        <span style={S.postTime}>{timeAgo}</span>
                    </div>
                </div>
                {/* 액션 */}
                <div style={S.postActions}>
                    <button onClick={() => onEdit(post)} style={S.actionBtn}><Edit3 size={15} color="#6366f1" /></button>
                    <button onClick={() => onDelete(post.id)} style={S.actionBtn}><Trash2 size={15} color="#ef4444" /></button>
                    {post.photos?.length > 1 && (
                        <button onClick={() => setExpanded(!expanded)} style={S.actionBtn}>
                            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </button>
                    )}
                </div>
            </div>
            {/* 태그 */}
            {post.tags?.length > 0 && (
                <div style={S.postTags}>
                    {post.tags.map((tag, i) => (
                        <span key={i} style={S.tag}>#{tag}</span>
                    ))}
                </div>
            )}
            {/* 확장 - 전체 사진 */}
            {expanded && post.photos?.length > 1 && (
                <div style={S.expandedPhotos}>
                    {post.photos.map((url, i) => (
                        <img key={i} src={url} alt="" style={S.expandedImg} />
                    ))}
                </div>
            )}
        </div>
    );
}

function formatTimeAgo(date) {
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}분 전`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}시간 전`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}일 전`;
    return `${Math.floor(days / 30)}개월 전`;
}

const S = {
    statsRow: { display: 'flex', gap: '12px', marginBottom: '16px' },
    statBox: { flex: 1, background: '#f8fafc', borderRadius: '12px', padding: '16px', textAlign: 'center' },
    statNum: { fontSize: '24px', fontWeight: '800', color: '#0f172a' },
    statLabel: { fontSize: '12px', color: '#94a3b8', marginTop: '2px' },

    addBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', background: '#f0f0ff', color: '#6366f1', border: '2px dashed #c7d2fe', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginBottom: '16px' },

    formCard: { background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', marginBottom: '16px' },
    formHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    formTitle: { fontSize: '16px', fontWeight: '700', margin: 0 },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' },
    formGroup: { marginBottom: '14px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
    input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', backgroundColor: '#fff', lineHeight: '1.6', boxSizing: 'border-box' },

    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' },
    photoItem: { aspectRatio: '1', borderRadius: '10px', overflow: 'hidden', position: 'relative' },
    photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
    photoRemove: { position: 'absolute', top: '4px', right: '4px', width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
    photoAdd: { aspectRatio: '1', borderRadius: '10px', border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f8fafc' },

    catGrid: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
    catPill: { padding: '6px 14px', borderRadius: '20px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: '500', cursor: 'pointer', outline: 'none' },
    catPillActive: { background: '#6366f1', color: '#fff', borderColor: '#6366f1' },

    submitBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },

    loadingText: { textAlign: 'center', color: '#94a3b8', padding: '40px', fontSize: '14px' },
    emptyState: { textAlign: 'center', padding: '48px 20px', background: '#fafbfc', borderRadius: '14px', border: '1px solid #f1f5f9' },

    postList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    postCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '14px', transition: 'box-shadow 0.2s' },
    postTop: { display: 'flex', gap: '14px' },
    postImgWrap: { width: '80px', height: '80px', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, position: 'relative', background: '#f1f5f9' },
    postImg: { width: '100%', height: '100%', objectFit: 'cover' },
    postImgBadge: { position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '700' },
    postInfo: { flex: 1, minWidth: 0 },
    postCategory: { display: 'inline-block', padding: '2px 10px', background: '#f0f0ff', color: '#6366f1', borderRadius: '10px', fontSize: '11px', fontWeight: '600', marginBottom: '4px' },
    postDesc: { fontSize: '13px', color: '#374151', lineHeight: '1.5', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
    postMeta: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px' },
    postStat: { display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', color: '#64748b' },
    postTime: { fontSize: '11px', color: '#94a3b8' },
    postActions: { display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 },
    actionBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px' },
    postTags: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' },
    tag: { fontSize: '11px', color: '#6366f1', fontWeight: '500' },
    expandedPhotos: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '6px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' },
    expandedImg: { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px' },
};

export default LabPortfolioPosts;
